import { useClaims } from '@lmt/unicorn-shared/app/features/auth/operations';
import { useEventCallback } from '@mui/material';
import { ResourceDto } from '@lmt/shared-types';
import { useScopedTranslation } from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { nameofFactory } from '@lmt/unicorn-shared/app/helpers/nameof';
import React, { FC, useMemo } from 'react';
import { TableDotMenu } from '../../../common/table/TableDotMenu';
import ArrowDown from '@lmt/unicorn-shared/app/icons/arrow-down.svg?react';
import {
  RowClickEvent,
  SortOrder,
} from '@lmt/unicorn-shared/app/common/devexpress/Grid/dx-grid';

import styles from '@lmt/unicorn-shared/app/components/patients/list/components/PatientsTable.module.scss';
import pageStyles from '../ResourceList.module.scss';
import { IconButton } from '../../../common/ui-kit/IconButton';
import { DataGrid } from '../../../common/dataGrid/DataGrid';

type Props = {
  noResults: boolean;
  data: ResourceDto[];
  onDelete(id: ResourceDto): void;
  onEdit(resource: ResourceDto): void;
  manualOrdering: boolean;
  onReorder: (value: ResourceDto, direction: 'up' | 'down') => void;
  translationScope:
    | 'resources.place.list'
    | 'resources.special_conditions.list'
    | 'resources.doctors.list';
};

const nameof = nameofFactory<ResourceDto>();

export const ResourcesTable: FC<Props> = React.memo(
  function ResourcesTable(props) {
    const i18n = useScopedTranslation(props.translationScope);

    const claims = useClaims();

    const columns = useMemo(() => {
      const columns = [];
      if (props.manualOrdering) {
        columns.push({
          key: 'manualOrderPosition',
          dataField: 'manualOrderPosition',
          caption: '',
          allowSorting: false,
          defaultSortOrder: 'asc' as SortOrder,
          width: '76px',
          cellRender: (cell: any) => (
            <>
              {cell.rowIndex > 0 ? (
                <IconButton
                  color="grey"
                  iconComponent={
                    <ArrowDown className={pageStyles.upsideDown} />
                  }
                  className={pageStyles.reorderButton}
                  onClick={() => props.onReorder(cell.data, 'up')}
                  testId={'reorder-up'}
                />
              ) : null}
              {cell.rowIndex != props.data.length - 1 ? (
                <IconButton
                  color="grey"
                  iconComponent={<ArrowDown />}
                  className={pageStyles.reorderButton}
                  onClick={() => props.onReorder(cell.data, 'down')}
                  testId={'reorder-down'}
                />
              ) : null}
            </>
          ),
        });
      }
      columns.push(
        {
          key: nameof('name'),
          caption: i18n.t('column_name'),
          allowSorting: true, //false, // !props.manualOrdering,
          defaultSortOrder: !props.manualOrdering
            ? ('asc' as SortOrder)
            : undefined,
          cellRender: (cell: any) => cell.data.name as any,
        },
        {
          key: 'actions',
          caption: '',
          width: '60px',
          cellRender: (cell: any) => (
            <div
              onClick={(event: any) => event.stopPropagation()}
              style={{ margin: -6 }}
            >
              <TableDotMenu
                options={[
                  {
                    title: i18n.t('edit_button'),
                    onClick: () => props.onEdit(cell.data),
                    isHidden: !claims.permissions.manageResources,
                  },
                  {
                    title: i18n.t('remove_button'),
                    onClick: () => props.onDelete(cell.data),
                    isDeleteItem: true,
                    isUnderLine: true,
                    isHidden: !claims.permissions.manageResources,
                  },
                ]}
              />
            </div>
          ),
        },
      );
      return columns;
    }, [props.manualOrdering]);

    const onRowClick = useEventCallback((e: RowClickEvent) => {
      if (!claims.permissions.manageResources) {
        return;
      }
      props.onEdit(e.data);
    });

    if (props.noResults) {
      return (
        <div className={styles.emptyList}>
          <div className={styles.emptyListText}>{i18n.t('empty')}</div>
        </div>
      );
    }

    return (
      <DataGrid
        keyExpr="id"
        dataSource={props.data}
        onRowClick={onRowClick}
        sorting={{ mode: 'none', showSortIndexes: false }}
        remoteOperations={{ paging: false, sorting: false }}
        columns={columns}
      />
    );
  },
);
